import NoData from "@/components/common/NoData";
import ChallengeDefaultCard from "./components/cards/DefaultCard";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "react-error-boundary";
import IconStar from "@/assets/icons/star-filled.svg?react";

import DefaultCardLoading from "./components/cards/DefaultCardLoading";
import { logError } from "@/components/error-hanlder/error-handler.lib";
import { PropsWithChildren, Suspense, useEffect } from "react";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { useChallenges } from "./api";
import { toast } from "sonner";
import { useInfiniteQueryFlatted } from "@/shared/hooks/useInfiniteQueryFlatted";
import InfiniteScrollList, {
  checkFetchMore,
} from "@/components/base/InfiniteScrollList";

const ChallengeListWrapper = (props: PropsWithChildren) => {
  return <div className="grid gap-3">{props.children}</div>;
};

const ErrorHandler = () => {
  const { t } = useTranslation();

  useEffect(() => {
    toast.error(t("challenge_fetching_error"));
  }, [t]);

  return (
    <NoData
      title={t("challenge_no_data_title")}
      description={t("challenge_no_data_description")}
      image={<IconStar color="white" width={48} height={48} />}
    />
  );
};

export default function ChallengesAll() {
  return (
    <ErrorBoundary FallbackComponent={ErrorHandler} onError={logError}>
      <Suspense
        fallback={
          <ChallengeListWrapper>
            {Array.from({ length: 3 }).map((_, idx) => (
              <DefaultCardLoading key={idx} />
            ))}
          </ChallengeListWrapper>
        }
      >
        <BaseChallengesAll />
      </Suspense>
    </ErrorBoundary>
  );
}

function BaseChallengesAll() {
  const { t } = useTranslation();

  const queryResult = useSuspenseInfiniteQuery(useChallenges());

  const challenges = useInfiniteQueryFlatted(queryResult.data);

  return (
    <ChallengeListWrapper>
      <InfiniteScrollList
        isFetchingNextPage={queryResult.isFetchingNextPage}
        fetchNextPage={queryResult.fetchNextPage}
        canFetchMore={checkFetchMore(queryResult)}
        className="gap-3"
        placeholder={<DefaultCardLoading />}
      >
        {challenges.length === 0 ? (
          <div className="flex-1 flex-center min-h-[60vh]">
            <NoData
              title={t("challenge_no_data_title")}
              description={t("challenge_no_data_description")}
              image={<IconStar color="white" width={48} height={48} />}
            />
          </div>
        ) : (
          challenges.map((item) => (
            <ChallengeDefaultCard
              id={item.id}
              key={item.id}
              title={item.name}
              srcImg={item.images[0]}
              status={item.status}
              deadline={item.deadline}
              gainedAmount={item.gained_amount}
              isNew={item.is_new}
              opportunityFee={item.opportunity_fee}
              participantFee={item.participant_fee}
              targetAmount={item.target_amount}
            />
          ))
        )}
      </InfiniteScrollList>
    </ChallengeListWrapper>
  );
}
